import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { TACTIL } from '#components/mobile/ui/tokens';
import { useServerURL } from '#components/ServerContext';

import { Galeon } from './Galeon';
import { BarraTravesia } from './Paso0Bienvenida';
import { buscarPredefinida, PORTADAS } from './portada';

/** Lo que tarda el barco en bajar por la rampa hasta el agua. */
export const DURACION_BOTADURA = 2200;

export type DatosBarco = {
  nombre: string;
  portadaId: string;
  /** En el dispositivo y en la nube; si no, solo en este dispositivo. */
  nube: boolean;
  /** Con la «Primera travesía» de Gavi (por defecto) o sin ella. */
  conGavi?: boolean;
};

/**
 * Modo «Primera travesía» (paso 1): barra con «Paso 1 de 8» y «Saltar», y
 * Gavi en el astillero y en la botadura. Solo cuando se llega del paso 0.
 */
export type AstilleroTravesia = {
  paso: number;
  /** Quita la barra y a Gavi; el astillero sigue igual. */
  onSaltar: () => void;
};

type AstilleroProps = {
  colorInicial: string;
  /** Crea el presupuesto y lo abre (se llama al tocar «Zarpar»). */
  onBotar: (datos: DatosBarco) => Promise<void>;
  onCerrar: () => void;
  travesia?: AstilleroTravesia;
};

/**
 * Astillero de Rumbo: crear un presupuesto es botar un barco nuevo. Se le da
 * nombre, colores y amarre (solo en este dispositivo o también en la nube) y
 * baja por la rampa hasta el agua (la maqueta que aprobó Vadik, 12-oct-2026).
 * El presupuesto se crea al zarpar: al crearlo la app lo abre sola, y eso
 * cortaría la botadura.
 */
export function Astillero({
  colorInicial,
  onBotar,
  onCerrar,
  travesia,
}: AstilleroProps) {
  const { t } = useTranslation();
  const [nombre, setNombre] = useState('');
  const [portadaId, setPortadaId] = useState(colorInicial);
  // Sin servidor («No usar servidor») no hay nube a la que amarrar: se
  // empieza en «Solo en este móvil» para no prometer en la botadura un
  // amarre en la nube que luego no ocurre.
  const hayServidor = Boolean(useServerURL());
  const [nube, setNube] = useState(hayServidor);
  const [conGavi, setConGavi] = useState(true);
  const [botando, setBotando] = useState(false);
  const [enElAgua, setEnElAgua] = useState(false);
  const [zarpando, setZarpando] = useState(false);
  const vivo = useRef(true);
  useEffect(
    () => () => {
      vivo.current = false;
    },
    [],
  );

  const elegida = buscarPredefinida(portadaId) ?? PORTADAS[0];
  const colores = {
    vela: elegida.vela,
    casco: elegida.casco,
    bandera: elegida.bandera,
  };
  const nombreFinal = nombre.trim();

  const botar = () => {
    if (botando) {
      return;
    }
    setBotando(true);
    setEnElAgua(false);
    const quieto = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    window.setTimeout(
      () => vivo.current && setEnElAgua(true),
      quieto ? 0 : DURACION_BOTADURA,
    );
  };

  const zarpar = () => {
    if (zarpando) {
      return;
    }
    setZarpando(true);
    void onBotar({ nombre: nombreFinal, portadaId, nube, conGavi }).finally(
      () => vivo.current && setZarpando(false),
    );
  };

  if (botando) {
    return (
      <Botadura
        colores={colores}
        faro={nube}
        listo={enElAgua}
        titulo={
          nombreFinal
            ? t('{{name}} is afloat!', { name: nombreFinal })
            : t('Your boat is afloat!')
        }
        detalle={
          nube
            ? t(
                'Moored on your phone and in the cloud. You can open it on your other devices.',
              )
            : t('Moored only on this phone. Make a backup now and then.')
        }
        zarpando={zarpando}
        onZarpar={zarpar}
        travesia={travesia}
      />
    );
  }

  return (
    <View data-testid="astillero" style={pantalla}>
      {travesia && (
        <BarraTravesia
          paso={travesia.paso}
          onSaltar={travesia.onSaltar}
          style={{ position: 'sticky', top: 0, zIndex: 2 }}
        />
      )}
      <View
        style={{
          flexShrink: 0,
          padding: travesia
            ? '10px 16px 14px'
            : 'calc(18px + env(safe-area-inset-top)) 16px 14px',
          background: 'linear-gradient(180deg, #0d1526 0%, #1d3550 100%)',
        }}
      >
        <h1 style={titulo}>
          <Trans>Shipyard</Trans>
        </h1>
        <Text style={{ color: '#9aa6bd', fontSize: 13 }}>
          <Trans>Your new budget</Trans>
        </Text>
      </View>

      <View
        style={{
          gap: 14,
          // Que no se encoja cuando no cabe: la pantalla ya se desplaza.
          flexShrink: 0,
          padding: '14px 16px calc(24px + env(safe-area-inset-bottom))',
          maxWidth: 520,
          width: '100%',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {travesia && (
          <Bocadillo
            variante="astillero"
            estado="vuela"
            texto={t(
              'Every budget is a boat. I recommend the cloud: if your phone falls into the sea, the boat carries on.',
            )}
            data-testid="astillero-gavi"
          />
        )}
        <View
          aria-hidden
          style={{
            position: 'relative',
            height: 170,
            borderRadius: 16,
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(180deg, #1d3550 0%, #14424a 100%)',
          }}
        >
          <View
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 44,
              backgroundColor: '#0f3340',
            }}
          />
          <View className={meceClase}>
            <Galeon {...colores} width={170} />
          </View>
        </View>

        <label htmlFor="astillero-nombre" style={etiqueta}>
          <Trans>Boat name</Trans>
        </label>
        <input
          id="astillero-nombre"
          data-testid="astillero-nombre"
          value={nombre}
          maxLength={60}
          placeholder={t('My budget')}
          onChange={e => setNombre(e.currentTarget.value)}
          className={css({
            minHeight: TACTIL,
            boxSizing: 'border-box',
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1.5px solid #25455c',
            backgroundColor: '#12283a',
            color: '#e8eef7',
            fontSize: 16,
            fontFamily: 'inherit',
            '::placeholder': { color: '#7d8aa3' },
            ':focus': { outline: '2px solid #8fd3b0', outlineOffset: 1 },
          })}
        />

        <Text style={etiqueta}>
          <Trans>Colours</Trans>
        </Text>
        <View
          role="group"
          aria-label={t('Colours')}
          style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}
        >
          {PORTADAS.map(p => (
            <Button
              key={p.id}
              variant="bare"
              data-testid={`astillero-color-${p.id}`}
              aria-label={t('Colour {{name}}', { name: p.id })}
              aria-pressed={p.id === portadaId}
              onPress={() => setPortadaId(p.id)}
              style={{
                width: TACTIL,
                height: TACTIL,
                borderRadius: 999,
                border: `2px solid ${p.id === portadaId ? '#e8eef7' : 'transparent'}`,
                padding: 0,
              }}
            >
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: `linear-gradient(135deg, ${p.vela} 50%, ${p.casco} 50%)`,
                }}
              />
            </Button>
          ))}
        </View>

        <Text style={etiqueta}>
          <Trans>Where does it moor?</Trans>
        </Text>
        <Amarre
          testId="astillero-nube"
          icono="nube"
          tono="#8fd3b0"
          elegido={nube}
          titulo={t('On your phone and in the cloud')}
          recomendado
          detalle={t(
            'You see it on all your devices and can share it. If you lose your phone, you lose nothing.',
          )}
          onPress={() => setNube(true)}
        />
        <Amarre
          testId="astillero-local"
          icono="movil"
          tono="#f3d9a4"
          elegido={!nube}
          titulo={t('Only on this phone')}
          detalle={t(
            'It never leaves this device. You can upload it to the cloud later from Settings.',
          )}
          onPress={() => setNube(false)}
        />
        {!nube && (
          <Text
            role="note"
            style={{
              fontSize: 13,
              color: '#f3d9a4',
              backgroundColor: '#2a2417',
              borderRadius: 10,
              padding: '9px 11px',
            }}
          >
            <Trans>
              If you delete the app or change phones, this boat is lost. Make a
              backup now and then.
            </Trans>
          </Text>
        )}

        <Text style={etiqueta}>
          <Trans>Who do you set sail with?</Trans>
        </Text>
        <Amarre
          testId="astillero-con-gavi"
          icono="compass"
          tono="#8fd3b0"
          elegido={conGavi}
          titulo={t('Set sail with Gavi')}
          recomendado
          detalle={t(
            'I guide you step by step: your accounts, your categories and a goal. A few minutes.',
          )}
          onPress={() => setConGavi(true)}
        />
        <Amarre
          testId="astillero-sin-gavi"
          icono="velero"
          tono="#f3d9a4"
          elegido={!conGavi}
          titulo={t('On my own, without a guide')}
          detalle={t(
            'You start with a few basic categories. You can call me from Settings, First voyage.',
          )}
          onPress={() => setConGavi(false)}
        />

        <Button
          variant="bare"
          data-testid="astillero-botar"
          onPress={botar}
          style={botonMenta}
        >
          <Trans>Launch boat</Trans>
        </Button>
        <Button
          variant="bare"
          onPress={onCerrar}
          style={{ minHeight: TACTIL, color: '#8fd3b0', fontWeight: 700 }}
        >
          <Trans>Back to the harbour</Trans>
        </Button>
      </View>
    </View>
  );
}

type AmarreProps = {
  testId: string;
  icono: NombreIcono;
  tono: string;
  elegido: boolean;
  titulo: string;
  detalle: string;
  recomendado?: boolean;
  onPress: () => void;
};

function Amarre({
  testId,
  icono,
  tono,
  elegido,
  titulo,
  detalle,
  recomendado,
  onPress,
}: AmarreProps) {
  return (
    <Button
      variant="bare"
      data-testid={testId}
      aria-pressed={elegido}
      onPress={onPress}
      style={{
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 12,
        padding: 12,
        borderRadius: 12,
        textAlign: 'left',
        color: '#e8eef7',
        border: `1.5px solid ${elegido ? '#8fd3b0' : '#25455c'}`,
        backgroundColor: elegido ? '#143a40' : '#12283a',
      }}
    >
      <View
        aria-hidden
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 10,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0b1d2a',
          color: tono,
        }}
      >
        <Icono nombre={icono} size={20} />
      </View>
      <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Text style={{ fontWeight: 800, fontSize: 15.5 }}>{titulo}</Text>
          {recomendado && (
            <Text
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: '#0b1d2a',
                backgroundColor: '#8fd3b0',
                borderRadius: 999,
                padding: '1px 8px',
              }}
            >
              <Trans>Recommended</Trans>
            </Text>
          )}
        </View>
        <Text style={{ fontSize: 13.5, color: '#b9c6d8' }}>{detalle}</Text>
      </View>
    </Button>
  );
}

type BotaduraProps = {
  colores: { vela: string; casco: string; bandera: string };
  faro: boolean;
  listo: boolean;
  titulo: string;
  detalle: string;
  zarpando: boolean;
  onZarpar: () => void;
  travesia?: AstilleroTravesia;
};

/** El barco baja por la rampa, salpica y, si va a la nube, se enciende el faro. */
function Botadura({
  colores,
  faro,
  listo,
  titulo,
  detalle,
  zarpando,
  onZarpar,
  travesia,
}: BotaduraProps) {
  const { t } = useTranslation();
  return (
    <View
      data-testid="botadura"
      style={{
        ...pantalla,
        overflow: 'hidden',
        background:
          'linear-gradient(180deg, #0d1526 0%, #1d3550 55%, #14424a 55%, #0b1d2a 100%)',
      }}
    >
      {travesia && (
        <BarraTravesia
          paso={travesia.paso}
          onSaltar={travesia.onSaltar}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}
        />
      )}
      {faro && (
        <svg
          aria-hidden
          viewBox="0 0 200 120"
          width={200}
          height={120}
          style={{ position: 'absolute', right: 12, top: '12%' }}
        >
          <defs>
            <linearGradient id="astillero-haz" x1="1" x2="0" y1="0" y2="0">
              <stop offset="0" stopColor="#ffe9a8" stopOpacity="0.7" />
              <stop offset="1" stopColor="#ffe9a8" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M176 22 L10 4 L10 44 L176 26 Z"
            fill="url(#astillero-haz)"
            className={haz}
          />
          <rect x="170" y="24" width="13" height="80" fill="#dff3e8" />
          <rect x="170" y="46" width="13" height="9" fill="#e0766b" />
          <rect x="170" y="70" width="13" height="9" fill="#e0766b" />
          <path d="M167 24 L176.5 11 L186 24 Z" fill="#2e8b62" />
          <circle cx="176.5" cy="19" r="3" fill="#ffe9a8" />
        </svg>
      )}
      <View
        aria-hidden
        style={{
          position: 'absolute',
          left: -10,
          top: 'calc(55% - 60px)',
          width: 260,
          height: 14,
          backgroundColor: '#6b4f3a',
          transform: 'rotate(14deg)',
          transformOrigin: 'left center',
        }}
      />
      <View
        aria-hidden
        className={barcoBajando}
        style={{
          position: 'absolute',
          left: 14,
          top: 'calc(55% - 150px)',
        }}
      >
        <Galeon {...colores} width={140} />
      </View>
      <View
        aria-hidden
        className={salpica}
        style={{
          position: 'absolute',
          left: 160,
          top: 'calc(55% + 30px)',
          width: 130,
          height: 18,
          borderRadius: '50%',
          border: '2px solid #dff3e8',
          opacity: 0,
        }}
      />
      <View
        role="status"
        style={{
          position: 'absolute',
          left: 16,
          right: 16,
          bottom: 'calc(24px + env(safe-area-inset-bottom))',
          gap: 10,
          maxWidth: 520,
          margin: '0 auto',
          opacity: listo ? 1 : 0,
          transition: 'opacity .4s',
        }}
      >
        {listo && (
          <>
            {travesia && (
              <Bocadillo
                variante="astillero"
                estado="aplaude"
                texto={t('Not a drop inside. Good hull.')}
                style={{ marginBottom: 4 }}
                data-testid="botadura-gavi"
              />
            )}
            <Text
              style={{
                fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif",
                fontSize: 24,
                fontWeight: 700,
                color: '#dff3e8',
              }}
            >
              {titulo}
            </Text>
            <Text style={{ fontSize: 14, color: '#cfd8e6' }}>{detalle}</Text>
            <Button
              variant="bare"
              data-testid="botadura-zarpar"
              isDisabled={zarpando}
              onPress={onZarpar}
              style={botonMenta}
            >
              <Trans>Set sail</Trans>
            </Button>
          </>
        )}
      </View>
    </View>
  );
}

const pantalla = {
  position: 'fixed',
  inset: 0,
  // Por encima del aviso de sesión de la gestión (zIndex 4000), que tapaba
  // «Saltar» con servidor.
  zIndex: 4001,
  overflowY: 'auto',
  overflowX: 'hidden',
  background: '#0b1d2a',
  color: '#e8eef7',
} as const;

const titulo = {
  margin: 0,
  fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif",
  fontSize: 23,
  fontWeight: 700,
  color: '#dff3e8',
} as const;

const etiqueta = {
  fontSize: 13,
  fontWeight: 600,
  color: '#9aa6bd',
} as const;

const botonMenta = {
  minHeight: 50,
  width: '100%',
  borderRadius: 12,
  backgroundColor: '#8fd3b0',
  color: '#0b1d2a',
  fontSize: 16,
  fontWeight: 800,
} as const;

const mece = keyframes({
  '0%, 100%': { transform: 'rotate(-1.5deg) translateY(0)' },
  '50%': { transform: 'rotate(1.5deg) translateY(-3px)' },
});

const meceClase = css({
  position: 'relative',
  animation: `${mece} 2.6s ease-in-out infinite`,
  transformOrigin: '50% 90%',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const desliza = keyframes({
  '0%': { transform: 'translate(0, 0) rotate(14deg)' },
  '60%': { transform: 'translate(150px, 120px) rotate(14deg)' },
  '80%': { transform: 'translate(170px, 128px) rotate(-3deg)' },
  '100%': { transform: 'translate(170px, 124px) rotate(0deg)' },
});

const barcoBajando = css({
  animation: `${desliza} ${DURACION_BOTADURA}ms cubic-bezier(.5,0,.3,1) forwards`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    transform: 'translate(170px, 124px)',
  },
});

const ola = keyframes({
  '0%': { opacity: 0.8, transform: 'scale(.3)' },
  '100%': { opacity: 0, transform: 'scale(1.6)' },
});

const salpica = css({
  animation: `${ola} 1.2s ${DURACION_BOTADURA * 0.6}ms ease-out forwards`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const gira = keyframes({
  from: { transform: 'rotate(-8deg)' },
  to: { transform: 'rotate(10deg)' },
});

const haz = css({
  transformOrigin: '176px 24px',
  animation: `${gira} 3s ease-in-out infinite alternate`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});
